// src/pages/profile/Profile.js
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import api from "../../api/axios";
import { EditUserSchema } from "../../utils/editUserSchema";
import { ThemeContext } from "../../context/ThemeContext";
import { useAuth } from "../../context/AuthContext";
import FloatingInput from "../../components/input/FloatingInput";
import CustomSelect from "../../components/customselect/CustomSelect";
import Toast from "../../components/common/Toast";
import profilepics from "../../images/profile-pics.png";
import "./profile.css";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { buildCompanyPath } from "../../utils/landingCompany";

export default function Profile() {
  const { t: trans } = useTranslation();
  const theme = useContext(ThemeContext);
  const { user, logoutUser, updateUser } = useAuth();
  const navigate = useNavigate();

  const [states, setStates] = useState([]);
  const [counties, setCounties] = useState([]);
  const [titleReps, setTitleReps] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemovingImage, setIsRemovingImage] = useState(false);
  const [isProfileLoaded, setIsProfileLoaded] = useState(false);
  const [isLoadingCounties, setIsLoadingCounties] = useState(false);

  const [profilePic, setProfilePic] = useState(profilepics);
  const [previewPic, setPreviewPic] = useState(null);
  const [passwords, setPasswords] = useState({
    password: "",
    confirmPassword: "",
  });

  const ucWords = (str = "") =>
    str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());

  // Function to format phone for display based on length
  const formatPhoneForDisplay = (digits) => {
    if (!digits) return "";

    if (digits.length <= 10) {
      // Format as XXX-XXX-XXXX for 10 digits
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else if (digits.length === 11) {
      // Format as X XXX-XXX-XXXX for 11 digits
      if (digits.length <= 1) {
        return digits;
      } else if (digits.length <= 4) {
        return `${digits.slice(0, 1)} ${digits.slice(1)}`;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(4)}`;
      } else {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(
          4,
          7
        )}-${digits.slice(7, 11)}`;
      }
    } else if (digits.length === 12) {
      // Format as XX XXX-XXX-XXXX for 12 digits
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 5) {
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      } else if (digits.length <= 8) {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5)}`;
      } else {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(
          5,
          8
        )}-${digits.slice(8, 12)}`;
      }
    }

    // For any other length, return digits as is
    return digits;
  };

  /** ✅ Validation schema */
  const schema = useMemo(
    () => EditUserSchema(states, counties, titleReps, trans),
    [states, counties, titleReps, trans]
  );

  const {
    register,
    handleSubmit,
    control,
    setError,
    reset,
    watch,
    clearErrors,
    setValue,
    getValues,
    trigger,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onChange",
  });

  const zipControllerRef = useRef(null);
  const stateId = watch("state");
  const countyId = watch("county");

  const [toaster, setToaster] = useState({
    message: "",
    type: "success",
    visible: false,
  });

  /** ✅ Load user profile on mount */
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/get-user-profile");
        if (res.data?.status === "success" && res.data?.data) {
          const userData = res.data.data;

          // Format phone number for display
          userData.phone = userData.phone
            ? formatPhoneForDisplay(userData.phone.replace(/\D/g, ""))
            : "";

          // Reset form with user data
          reset(userData);

          if (userData.profile_pic) {
            setProfilePic(
              `${process.env.REACT_APP_ASSETS_BASE_URL}/uploads/users/${userData.profile_pic
              }?t=${Date.now()}`
            );
          } else {
            setProfilePic(profilepics);
          }

          localStorage.setItem("user", JSON.stringify(userData));
        }
      } catch (err) {
        console.error("Error fetching user profile", err);
      } finally {
        setIsProfileLoaded(true);
      }
    })();
  }, [reset]);

  /** ✅ Cleanup blob URL on unmount */
  useEffect(() => {
    return () => {
      if (previewPic) URL.revokeObjectURL(previewPic);
    };
  }, [previewPic]);

  /** ✅ Load states dropdown */
  useEffect(() => {
    api
      .get(`/auth/states`, { params: { maincompanyid: theme.id } })
      .then((res) => setStates(res.data.data || []));
  }, [theme.id]);

  /** ✅ Load counties when state changes */
  useEffect(() => {
    const loadCounties = async () => {
      if (!stateId || stateId === "0" || stateId === 0) {
        setCounties([]);
        setValue("county", "");
        setValue("titlerep", "");
        setTitleReps([]);
        return;
      }

      setIsLoadingCounties(true);
      try {
        const res = await api.get(`/auth/counties`, {
          params: { maincompanyid: theme.id, stateId: stateId },
        });

        const countiesData = res.data.data || [];
        setCounties(countiesData);

        // Check if current county exists in new list
        const currentCounty = getValues("county");
        if (currentCounty) {
          const countyExists = countiesData.some(
            (c) => c.id.toString() === currentCounty.toString()
          );
          if (!countyExists) {
            setValue("county", "");
            setValue("titlerep", "");
            setTitleReps([]);
          }
        }
      } catch (err) {
        console.error("Error loading counties:", err);
        setCounties([]);
        setValue("county", "");
        setValue("titlerep", "");
        setTitleReps([]);
      } finally {
        setIsLoadingCounties(false);
      }
    };

    loadCounties();
  }, [stateId, theme.id, setValue, getValues]);

  /** ✅ Load title reps when county changes */
  useEffect(() => {
    const loadTitleReps = async () => {
      if (!stateId || !countyId || countyId === "0" || countyId === 0) {
        setTitleReps([]);
        setValue("titlerep", "");
        return;
      }

      try {
        const res = await api.get(`/auth/titlereps`, {
          params: {
            maincompanyid: theme.id,
            stateId,
            countyId,
          },
        });

        const titleRepsData = res.data.data || [];
        setTitleReps(titleRepsData);

        // Check if current titlerep exists in new list
        const currentTitleRep = getValues("titlerep");
        if (currentTitleRep && titleRepsData.length > 0) {
          const titleRepExists = titleRepsData.some(
            (t) => t.id.toString() === currentTitleRep.toString()
          );
          if (!titleRepExists) {
            setValue("titlerep", "");
          }
        } else {
          setValue("titlerep", "");
        }
      } catch (err) {
        console.error("Error loading title reps:", err);
        setTitleReps([]);
        setValue("titlerep", "");
      }
    };

    loadTitleReps();
  }, [stateId, countyId, theme.id, setValue, getValues]);

  /** ✅ Handle profile image upload */
  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    const previewURL = URL.createObjectURL(file);
    setPreviewPic(previewURL);
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("profile_image", file);

      const res = await api.put("/upload-profile-picture", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res.data?.status === "success") {
        const filename =
          res.data.fileName ||
          res.data.profile_pic ||
          res.data.fileUrl ||
          res.data.filePath;
        const fullUrl = `${process.env.REACT_APP_ASSETS_BASE_URL
          }/uploads/users/${filename}?t=${Date.now()}`;

        setTimeout(() => {
          setProfilePic(fullUrl);
          setPreviewPic(null);
        }, 500);

        // ✅ Update user object globally
        const currentFormData = getValues();
        const updatedUser = { ...currentFormData, profile_pic: filename };
        updateUser(updatedUser);
        setToaster({
          message: "Profile picture updated successfully!",
          type: "success",
          visible: true,
        });
      } else {
        setToaster({
          message: "Failed to updated profile picture!",
          type: "error",
          visible: true,
        });
      }
    } catch (err) {
      console.error("Upload error:", err);
      setToaster({
        message: "Failed to updated profile picture!",
        type: "error",
        visible: true,
      });
    } finally {
      setIsUploading(false);
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  };

  /** ✅ Handle profile image removal — reverts to the default image */
  const handleRemoveImage = async () => {
    if (
      !window.confirm(
        "Remove your profile picture and revert to the default image?"
      )
    ) {
      return;
    }

    setIsRemovingImage(true);
    try {
      const res = await api.delete("/remove-profile-picture");

      if (res.data?.status === "success") {
        setProfilePic(profilepics);
        setPreviewPic(null);

        // ✅ Update user object globally
        const currentFormData = getValues();
        const updatedUser = { ...currentFormData, profile_pic: "" };
        updateUser(updatedUser);

        setToaster({
          message: "Profile picture removed successfully!",
          type: "success",
          visible: true,
        });
      } else {
        setToaster({
          message: "Failed to remove profile picture!",
          type: "error",
          visible: true,
        });
      }
    } catch (err) {
      console.error("Remove image error:", err);
      setToaster({
        message: "Failed to remove profile picture!",
        type: "error",
        visible: true,
      });
    } finally {
      setIsRemovingImage(false);
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  };

  const handlePostalBlur = async (eOrValue) => {
    const zip =
      typeof eOrValue === "string" ? eOrValue : eOrValue.target?.value;
    if (!zip) return;

    if (zipControllerRef.current) {
      try {
        zipControllerRef.current.abort();
      } catch { }
    }

    const controller = new AbortController();
    zipControllerRef.current = controller;

    const stateid = getValues("state");
    const countyid = getValues("county");

    try {
      const res = await api.post(
        "/auth/verify-zip",
        { zipcode: zip, maincompanyid: theme.id, stateid, countyid },
        { signal: controller.signal }
      );

      if (res.data?.status === "fail") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_invalid"),
        });
      } else {
        const state = res.data?.data?.stateid;
        const county = res.data?.data?.countyid;

        // Set state first
        setValue("state", state || "");
        clearErrors("postal_code");

        // Set county after state is set
        setTimeout(() => {
          if (county) {
            setValue("county", county);
          }
        }, 300);
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("postal_code", {
          type: "manual",
          message: trans("register_zip_validation_failed"),
        });
      }
    }
  };

  /** ✅ Handle profile form save */
  const onSubmit = async (data) => {
    if (!isEditing) {
      console.log("⏸ Not editing — ignore submit");
      return;
    }

    try {
      // Clean phone number before sending (remove all non-digits)
      const cleanPhone = data.phone ? data.phone.replace(/\D/g, "") : "";

      const res = await api.put("/update-user-profile", {
        ...data,
        phone: cleanPhone,
        profile_pic: user?.profile_pic || "",
      });

      if (res.data?.status === "success") {
        // Format phone number for display
        const formattedPhone = formatPhoneForDisplay(res.data.data.phone);
        updateUser({
          ...res.data.data,
          phone: formattedPhone,
        });
        setToaster({
          message: "Profile updated successfully!",
          type: "success",
          visible: true,
        });
        setIsEditing(false);
      } else {
        setToaster({
          message: "Failed to update profile.",
          type: "error",
          visible: true,
        });
      }

      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    } catch (err) {
      console.error("❌ Update profile error:", err);
      // Surface specific validation messages (e.g. duplicate/invalid email)
      // instead of a generic one — the backend throws {status, message} for
      // these via updateUserProfile's email uniqueness check.
      const serverMessage = err?.response?.data?.message;
      if (serverMessage && /email/i.test(serverMessage)) {
        setError("email", { type: "manual", message: serverMessage });
      }
      setToaster({
        message: serverMessage || "Error updating profile.",
        type: "error",
        visible: true,
      });
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  };

  /** ✅ Change password handler */
  const onPasswordChange = async (e) => {
    e.preventDefault();
    if (!passwords.password || !passwords.confirmPassword) {
      setToaster({
        message: "Please fill out both fields.",
        type: "error",
        visible: true,
      });
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
      return;
    }

    if (passwords.password !== passwords.confirmPassword) {
      setToaster({
        message: "Passwords do not match!",
        type: "error",
        visible: true,
      });
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
      return;
    }

    try {
      const res = await api.post("/users/change-password", {
        password: passwords.password,
      });

      if (res.data?.status === "success") {
        setToaster({
          message: "Password updated successfully, Redirect to Login.",
          type: "success",
          visible: true,
        });
        setTimeout(async () => {
          setToaster((prev) => ({ ...prev, visible: false }));
          await logoutUser();
          navigate(buildCompanyPath(""));
        }, 2000);
        setPasswords({ password: "", confirmPassword: "" });
      } else {
        setToaster({
          message: "Error changing password.",
          type: "error",
          visible: true,
        });
        setTimeout(() => {
          setToaster((prev) => ({ ...prev, visible: false }));
        }, 2000);
      }
    } catch (err) {
      setToaster({
        message: "Error changing password.",
        type: "error",
        visible: true,
      });
      setTimeout(() => {
        setToaster((prev) => ({ ...prev, visible: false }));
      }, 2000);
    }
  };

  const handleImageSelect = () => {
    document.getElementById("imgInput").click();
  };

  // Function to format phone as user types
  const formatPhoneInput = (value) => {
    const digits = value.replace(/\D/g, "");

    if (digits.length <= 10) {
      // Format as XXX-XXX-XXXX for 10 digits
      if (digits.length <= 3) {
        return digits;
      } else if (digits.length <= 6) {
        return `${digits.slice(0, 3)}-${digits.slice(3)}`;
      } else {
        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(
          6,
          10
        )}`;
      }
    } else if (digits.length === 11) {
      // Format as X XXX-XXX-XXXX for 11 digits
      if (digits.length <= 1) {
        return digits;
      } else if (digits.length <= 4) {
        return `${digits.slice(0, 1)} ${digits.slice(1)}`;
      } else if (digits.length <= 7) {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(4)}`;
      } else {
        return `${digits.slice(0, 1)} ${digits.slice(1, 4)}-${digits.slice(
          4,
          7
        )}-${digits.slice(7, 11)}`;
      }
    } else if (digits.length === 12) {
      // Format as XX XXX-XXX-XXXX for 12 digits
      if (digits.length <= 2) {
        return digits;
      } else if (digits.length <= 5) {
        return `${digits.slice(0, 2)} ${digits.slice(2)}`;
      } else if (digits.length <= 8) {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(5)}`;
      } else {
        return `${digits.slice(0, 2)} ${digits.slice(2, 5)}-${digits.slice(
          5,
          8
        )}-${digits.slice(8, 12)}`;
      }
    }

    // If more than 12 digits, truncate to 12
    if (digits.length > 12) {
      const truncated = digits.slice(0, 12);
      return `${truncated.slice(0, 2)} ${truncated.slice(
        2,
        5
      )}-${truncated.slice(5, 8)}-${truncated.slice(8, 12)}`;
    }

    return digits;
  };

  /** ✅ Render */
  return (
    <>
      <div className="user-p">
        <div className="user-form">
          <div className="user-upload-img">
            <div className="user-info">
              <div className="user-p-pics">
                <div className={`uploader ${isUploading ? "uploading" : ""}`}>
                  <input
                    id="imgInput"
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleImageChange}
                  />
                  <label
                    htmlFor="imgInput"
                    className="upload-box"
                    aria-label="Upload image"
                  >
                    {!isUploading && (
                      <div className="upload-hint">
                        <svg
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          aria-hidden="true"
                        >
                          <path
                            d="M12 16v-8m0 0l-3 3m3-3l3 3M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <span>Upload</span>
                      </div>
                    )}
                    <img
                      id="preview"
                      src={
                        previewPic ||
                        (isProfileLoaded ? profilePic : profilepics)
                      }
                      alt="Profile"
                      style={{
                        display: "block",
                        objectFit: "cover",
                        borderRadius: "10px",
                      }}
                      onError={(e) => (e.target.src = profilepics)}
                    />

                    {isUploading && (
                      <div className="overlay">
                        <div className="spinner"></div>
                      </div>
                    )}
                  </label>
                  <div className="img-actions-overlay">
                    <button
                      className="img-action-btn"
                      onClick={handleImageSelect}
                      type="button"
                      disabled={isUploading || isRemovingImage}
                    >
                      Click here to change the image
                    </button>
                  </div>
                </div>
              </div>

              {isProfileLoaded && profilePic !== profilepics && (
                <div className="remove-image-action">
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={handleRemoveImage}
                    disabled={isUploading || isRemovingImage}
                  >
                    {isRemovingImage ? "Removing..." : "Remove Image"}
                  </button>
                </div>
              )}

              <div className="user-p-name">
                <h3>
                  Welcome,{" "}
                  {ucWords(
                    `${watch("first_name") || ""} ${watch("last_name") || ""}`
                  )}
                </h3>
              </div>
              <div className="user-p-desc">
                <p>
                  Click on image to change. This image will appear on your net
                  sheets.
                </p>
              </div>
            </div>
          </div>

          <div className="reg-f-container">
            {/* ✅ Form Fields */}
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* FirstName & lastName */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="First Name"
                    disabled={!isEditing}
                    {...register("first_name")}
                  />
                  {errors.first_name && (
                    <p className="field-error">{errors.first_name.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="Last Name"
                    disabled={!isEditing}
                    {...register("last_name")}
                  />
                  {errors.last_name && (
                    <p className="field-error">{errors.last_name.message}</p>
                  )}
                </div>
              </div>

              {/* Email & phone */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="Email"
                    disabled={!isEditing}
                    {...register("email")}
                  />
                  {errors.email && (
                    <p className="field-error">{errors.email.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="phone"
                    control={control}
                    render={({ field, fieldState }) => (
                      <div>
                        <FloatingInput
                          label="Phone Number"
                          value={field.value || ""}
                          disabled={!isEditing}
                          onChange={(e) => {
                            // Only allow digits, spaces, and dashes
                            const value = e.target.value;
                            const cleaned = value.replace(/[^\d\s-]/g, "");

                            // Format based on length
                            const formatted = formatPhoneInput(cleaned);

                            field.onChange(formatted);
                            trigger("phone");
                          }}
                          onBlur={() => {
                            field.onBlur();
                            trigger("phone");
                          }}
                          ref={field.ref}
                          placeholder="Enter 10 or 12 digits"
                          maxLength={20} // Fixed high value to allow typing
                        />
                        {fieldState.error && (
                          <p className="field-error">
                            {fieldState.error.message}
                          </p>
                        )}
                      </div>
                    )}
                  />
                </div>
              </div>

              {/* Address & city   */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="Business Address"
                    disabled={!isEditing}
                    {...register("mailing_address")}
                  />
                  {errors.mailing_address && (
                    <p className="field-error">
                      {errors.mailing_address.message}
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="City"
                    disabled={!isEditing}
                    {...register("city")}
                  />
                  {errors.city && (
                    <p className="field-error">{errors.city.message}</p>
                  )}
                </div>
              </div>

              {/* State & county */}
              <div className="frm-field-2">
                <div className="form-group">
                  <Controller
                    name="state"
                    control={control}
                    render={({ field }) => {
                      const options = (states || []).map((s) => ({
                        value: s.id,
                        label: s.statename,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          placeholder={selectedOption?.label || "Select State"}
                          value={selectedOption}
                          label={"State"}
                          onChange={(val) => field.onChange(val)}
                          disabled={!isEditing}
                        />
                      );
                    }}
                  />
                  {errors.state && (
                    <p className="field-error">{errors.state.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="county"
                    control={control}
                    render={({ field }) => {
                      const options = (counties || []).map((c) => ({
                        value: c.id,
                        label: c.countyname,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          placeholder={
                            selectedOption?.label ||
                            (isLoadingCounties ? "Loading..." : "Select County")
                          }
                          value={selectedOption}
                          label={"County"}
                          onChange={(val) => field.onChange(val)}
                          disabled={!isEditing || isLoadingCounties}
                          isLoading={isLoadingCounties}
                        />
                      );
                    }}
                  />
                  {errors.county && (
                    <p className="field-error">{errors.county.message}</p>
                  )}
                </div>
              </div>

              {/* ZipCode & titleRep */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="ZIP Code"
                    disabled={!isEditing}
                    {...register("postal_code")}
                    onBlur={handlePostalBlur}
                    maxLength={5}
                  />
                  {errors.postal_code && (
                    <p className="field-error">{errors.postal_code.message}</p>
                  )}
                </div>
                <div className="form-group">
                  <Controller
                    name="titlerep"
                    control={control}
                    render={({ field }) => {
                      const options = (titleReps || []).map((t) => ({
                        value: t.id,
                        label: t.name,
                      }));

                      const selectedOption =
                        options.find(
                          (opt) => Number(opt.value) === Number(field.value)
                        ) || null;

                      return (
                        <CustomSelect
                          options={options}
                          placeholder={
                            selectedOption?.label || "Select Title Rep"
                          }
                          value={selectedOption}
                          label={"Title Rep"}
                          onChange={(val) => field.onChange(val)}
                          disabled={!isEditing}
                        />
                      );
                    }}
                  />
                  {errors.titlerep && (
                    <p className="field-error">{errors.titlerep.message}</p>
                  )}
                </div>
              </div>

              {/* Office */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="Brokerage Name?"
                    disabled={!isEditing}
                    {...register("office_name")}
                  />
                </div>
                <div className="form-group">

                </div>
              </div>

              {/* DRE & Corporate DRE Number */}
              <div className="frm-field-2">
                <div className="form-group">
                  <FloatingInput
                    label="DRE Number"
                    disabled={!isEditing}
                    {...register("dre_nmls")}
                  />
                </div>
                <div className="form-group">
                  <FloatingInput
                    label="Corporate DRE Number"
                    disabled={!isEditing}
                    {...register("corporate_nmls")}
                  />
                </div>
              </div>

              {/* ✅ Form Buttons */}
              <div className="full-width-container-btn">
                <div className="left-btn-container">
                  {isEditing && (
                    <button
                      type="button"
                      onClick={() => {
                        setIsEditing(false);
                        // Reset form to original user data
                        reset(user);
                      }}
                      className="submit-btn btn-bg inpt-btn w-120 discard-btn"
                    >
                      Discard
                    </button>
                  )}
                </div>
                <div className="right-btn-container">
                  {isEditing && (
                    <button
                      type="submit"
                      disabled={isSubmitting || Object.keys(errors).length > 0}
                      className="submit-btn btn-bg inpt-btn w-190"
                      style={{ backgroundColor: theme.primary_color }}
                    >
                      {isSubmitting ? "Saving..." : "Save Changes"}
                    </button>
                  )}
                </div>
              </div>
            </form>

            {!isEditing && (
              <div className="full-width-container-btn">
                <div className="right-btn-container">
                  <button
                    type="button"
                    onClick={() => setIsEditing(true)}
                    className="submit-btn btn-bg inpt-btn w-190"
                    style={{ backgroundColor: theme.primary_color }}
                  >
                    Update my profile
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ✅ Change Password */}
        <div className="user-form change-password-container">
          <div className="user-upload-img"></div>
          <div className="reg-f-containe">
            <div className="user-info top-info-line">
              <div className="user-p-name">
                <h3>Change your password</h3>
              </div>
              <div className="user-p-desc">
                <p>
                  For security reasons, please change your password every 3
                  months.
                </p>
              </div>
              <div className="user-form">
                <form onSubmit={onPasswordChange} className="reg-f-container">
                  <div className="frm-field-2">
                    <div className="form-group">
                      <FloatingInput
                        label="New password"
                        type="password"
                        value={passwords.password}
                        onChange={(e) =>
                          setPasswords((prev) => ({
                            ...prev,
                            password: e.target.value,
                          }))
                        }
                      />
                    </div>
                    <div className="form-group">
                      <FloatingInput
                        label="Confirm password"
                        type="password"
                        value={passwords.confirmPassword}
                        onChange={(e) =>
                          setPasswords((prev) => ({
                            ...prev,
                            confirmPassword: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="full-width-container-btn">
                    <div className="right-btn-container">
                      <button
                        type="submit"
                        className="submit-btn btn-bg inpt-btn w-190"
                        style={{ backgroundColor: theme.primary_color }}
                      >
                        Update my password
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        <Toast
          message={toaster.message}
          type={toaster.type}
          visible={toaster.visible}
        />
      </div>
    </>
  );
}
